// src/components/Header.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, ShoppingCart, User, ShoppingBag, 
  BarChart3, Menu, X, LogIn, ChevronDown, 
  Loader2, Sparkles, XCircle
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';

// Category structure (shared)
export const categoryStructure = {
  "Kitchen": ["Faucets", "Sinks", "Countertops", "Cabinets", "Kitchen Appliances", "Kitchen Accessories", "Kitchen Lighting"],
  "Bathroom": ["Sinks", "Faucets", "Showers & Tubs", "Tiles & Flooring", "Bathroom Furniture", "Bathroom Lighting", "Bathroom Accessories"],
  "Living Room": ["Flooring", "Walls & Ceilings", "Lighting", "Furniture", "Home Decor"],
  "Workroom / Office": ["Furniture", "Lighting", "Office Supplies", "Walls & Flooring"],
  "Flooring": ["Indoor", "Outdoor/Balcony", "Kitchen & Bathroom"],
  "Ceiling / Lighting": ["Lighting Fixtures", "Ceiling Materials"],
  "Balcony / Outdoor": ["Flooring", "Furniture", "Lighting", "Decor & Gardening"],
  "Hardware / Accessories": ["Door & Window Handles", "Cabinet Handles", "Hardware Accessories"]
};

// Group categories into 4 groups of 2 (groups 2-5)
const categoryGroups = [
  ["Kitchen", "Bathroom"],
  ["Living Room", "Workroom / Office"],
  ["Flooring", "Ceiling / Lighting"],
  ["Balcony / Outdoor", "Hardware / Accessories"]
];

const ProductsDropdown = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateTo = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const getMainCategoryPath = (main) => 
    `/category/${main.toLowerCase().replace(/\s+/g, '-')}`;
  
  const getSubCategoryPath = (main, sub) => {
    const mainKey = main.toLowerCase().replace(/\s+/g, '-');
    const subKey = sub.toLowerCase().replace(/\s+/g, '-').replace(/[&/]/g, '');
    return `/subcategory/${mainKey}/${subKey}`;
  };

  if (isMobile) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex justify-between items-center py-2 text-left"
        >
          Products
          <ChevronDown size={16} />
        </button>

        {isOpen && (
          <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
            <div className="px-4 py-2 space-y-1">
              <button onClick={() => navigateTo('/products')} className="block w-full text-left text-sm hover:bg-gray-50 py-1">All Products</button>
              <button onClick={() => navigateTo('/latest')} className="block w-full text-left text-sm hover:bg-gray-50 py-1">Latest Products</button>
              <button onClick={() => navigateTo('/popular')} className="block w-full text-left text-sm hover:bg-gray-50 py-1">Popular Products</button>
              <button onClick={() => navigateTo('/recommended')} className="block w-full text-left text-sm hover:bg-gray-50 py-1">Recommended Products</button>
            </div>
            <div className="border-t border-gray-200 my-2 mx-4"></div>

            {Object.entries(categoryStructure).map(([main, subs]) => (
              <div key={main} className="px-4 py-1">
                <button 
                  onClick={() => navigateTo(getMainCategoryPath(main))}
                  className="block w-full text-left font-medium hover:bg-gray-50 py-1"
                >
                  {main}
                </button>
                <div className="ml-3 mt-1 space-y-1">
                  {subs.map(sub => (
                    <button
                      key={sub}
                      onClick={() => navigateTo(getSubCategoryPath(main, sub))}
                      className="block w-full text-left text-gray-600 hover:bg-gray-50 py-1 text-sm pl-2"
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 hover:text-blue-600"
      >
        Products
        <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-6 overflow-auto max-h-[80vh]">
          <div className="grid grid-cols-5 gap-40">
            <div>
              <div className="font-medium text-gray-900 mb-3">Browse Products</div>
              <div className="space-y-2">
                <button onClick={() => navigateTo('/products')} className="block w-full text-left text-sm text-gray-700 hover:text-blue-600">All Products</button>
                <button onClick={() => navigateTo('/latest')} className="block w-full text-left text-sm text-gray-700 hover:text-blue-600">Latest Products</button>
                <button onClick={() => navigateTo('/popular')} className="block w-full text-left text-sm text-gray-700 hover:text-blue-600">Popular Products</button>
                <button onClick={() => navigateTo('/recommended')} className="block w-full text-left text-sm text-gray-700 hover:text-blue-600">Recommended Products</button>
              </div>
            </div>

            {categoryGroups.map((group, idx) => (
              <div key={idx} className="space-y-6">
                {group.map(main => (
                  <div key={main} className="flex flex-col">
                    <div 
                      className="font-medium text-gray-900 mb-2 cursor-pointer hover:text-blue-600"
                      onClick={() => navigateTo(getMainCategoryPath(main))}
                    >
                      {main}
                    </div>
                    <div className="space-y-1.5 text-sm text-gray-600 flex flex-col items-start">
                      {categoryStructure[main].map(sub => (
                        <button
                          key={sub}
                          onClick={() => navigateTo(getSubCategoryPath(main, sub))}
                          className="block w-full text-left hover:text-blue-600 py-0.5 px-1 rounded hover:bg-gray-50"
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { 
    cart, 
    user, 
    signOutUser, 
    openAuthModal,
    performAISearch,
    searchResults,
    searchLoading,
    searchIntent,
    searchSuggestions,
    lastSearchQuery
  } = useApp();
  
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // AI search handling
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (searchTerm.trim()) {
      const timeout = setTimeout(() => {
        performAISearch(searchTerm);
        setShowSearchResults(true);
      }, 500); // 500ms debounce
      setSearchTimeout(timeout);
    } else {
      setShowSearchResults(false);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, performAISearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (product) => {
    navigate(`/product/${product.id}`);
    setShowSearchResults(false);
    setSearchTerm('');
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    performAISearch(suggestion);
    setShowSearchResults(true);
  };

  const handleLogout = async () => {
    await signOutUser();
    setProfileDropdownOpen(false);
  };

  const handleSellerLogin = () => {
    console.log('Header - handleSellerLogin called');
    console.log('openAuthModal function:', openAuthModal);
    try {
      openAuthModal('seller');
      console.log('openAuthModal called successfully');
    } catch (error) {
      console.error('Error calling openAuthModal:', error);
    }
  };

  const handleBuyerLogin = () => {
    openAuthModal('buyer');
  };

  const renderAuthButtons = () => (
    <>
      <button
        onClick={handleSellerLogin}
        className="text-xs text-gray-700 hover:text-gray-900 font-light"
      >
        Seller Login
      </button>
      <button
        onClick={handleBuyerLogin}
        className="text-xs text-gray-700 hover:text-gray-900 font-light"
      >
        Buyer Login
      </button>
    </>
  );

  const renderProfileDropdown = () => {
    if (!user) {
      console.log('Header - No user, returning null');
      return null;
    }
    
    console.log('Header - renderProfileDropdown - user:', user);
    console.log('Header - user.userType:', user.userType);
    console.log('Header - user.email:', user.email);
    
    const isSeller = user.userType === 'seller';
    const isBuyer = user.userType === 'buyer';
    
    console.log('Header - isSeller:', isSeller);
    console.log('Header - isBuyer:', isBuyer);
    
    // Debug test
    if (user.userType === 'seller') {
      console.log('Header - User is seller, should show Dashboard and Profile Settings');
    } else if (user.userType === 'buyer') {
      console.log('Header - User is buyer, should show Profile Settings, Order Management, Cart');
    } else {
      console.log('Header - User type is unknown:', user.userType);
    }
    
    // Force dropdown menu display test
    console.log('Header - profileDropdownOpen:', profileDropdownOpen);
    console.log('Header - Will render dropdown:', profileDropdownOpen ? 'YES' : 'NO');
    
    return (
      <div className="relative" ref={dropdownRef}>
        <button 
          onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          className="flex items-center gap-2 p-2 text-gray-700 hover:text-blue-600"
        >
          <User size={16} />
          <span className="text-xs hidden sm:inline">
            {user.firstName || user.email.split('@')[0]}
          </span>
          {isSeller && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
              Seller
            </span>
          )}
          {isBuyer && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
              Buyer
            </span>
          )}
          <ChevronDown size={14} className="hidden sm:inline" />
        </button>
        
        {profileDropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            {isSeller && (
              <button
                onClick={() => {
                  navigate('/seller/dashboard');
                  setProfileDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <BarChart3 size={14} />
                Dashboard
              </button>
            )}
            
            <button
              onClick={() => {
                navigate('/profile');
                setProfileDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <User size={14} />
              Profile Settings
            </button>
            
            {isBuyer && (
              <>
                <button
                  onClick={() => {
                    navigate('/buyer-orders');
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <ShoppingBag size={14} />
                  Order Management
                </button>
                <button
                  onClick={() => {
                    navigate('/cart');
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <ShoppingCart size={14} />
                  Cart
                </button>
              </>
            )}
            
            <div className="border-t border-gray-200 my-1"></div>
            
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <LogIn size={14} />
              Logout
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="bg-apple-bg border-b border-apple-divider sticky top-0 z-50 backdrop-blur-md bg-opacity-95">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo: using Link */}
          <Link to="/" className="text-body font-sf-pro text-apple-text-primary apple-hover">
            Namsan Bro
          </Link>
          
          <nav className="hidden md:flex items-center space-x-5">
            <ProductsDropdown />
            <div className="w-px h-4 bg-gray-300 mx-2"></div>
            <div className="relative" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search with AI... (e.g., 'modern kitchen faucet')"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-10 py-1 text-sm border border-gray-300 rounded-full focus:ring-1 focus:ring-blue-400 focus:border-blue-400 w-64"
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" size={16} />
                )}
                {searchTerm && !searchLoading && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setShowSearchResults(false);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </form>

              {/* AI search results dropdown */}
              {showSearchResults && (searchResults.length > 0 || searchSuggestions.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  {/* Search intent display */}
                  {searchIntent && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                      <div className="flex items-center gap-2 text-sm text-blue-700">
                        <Sparkles size={14} />
                        <span className="font-medium">AI Search Intent:</span>
                        <span>{searchIntent}</span>
                      </div>
                    </div>
                  )}

                  {/* Search results */}
                  {searchResults.length > 0 && (
                    <div className="py-2">
                      <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Search Results ({searchResults.length})
                      </div>
                      {searchResults.slice(0, 5).map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleSearchResultClick(product)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                        >
                          <img
                            src={product.images?.[0] || '/placeholder-product.jpg'}
                            alt={product.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {product.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {product.category} • ${product.moqPrices?.[0]?.price || 0}
                            </div>
                            {product.matchReason && (
                              <div className="text-xs text-blue-600 mt-1">
                                {product.matchReason}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            {Math.round(product.relevanceScore * 100)}%
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Search suggestions */}
                  {searchSuggestions.length > 0 && (
                    <div className="py-2 border-t border-gray-100">
                      <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Suggestions
                      </div>
                      {searchSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Search size={14} className="text-gray-400" />
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No results */}
                  {searchResults.length === 0 && searchSuggestions.length === 0 && !searchLoading && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Search size={24} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No products found for "{lastSearchQuery}"</p>
                      <p className="text-xs text-gray-400 mt-1">Try different keywords or browse categories</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
          
          <div className="flex items-center gap-4">
            {/* Cart: using Link */}
            <Link to="/cart" className="relative p-2 text-gray-700 hover:text-blue-600">
              <ShoppingCart size={20} />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.filter(item => !item.id?.startsWith('temp-')).length} {/* Exclude temporary items */}
                </span>
              )}
            </Link>
            
            {user ? (
              <div className="flex items-center gap-4">
                {renderProfileDropdown()}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-4">
                {renderAuthButtons()}
              </div>
            )}
            
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="px-4 space-y-4">
              <div className="relative" ref={searchRef}>
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search with AI..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 pr-10 py-2 border border-gray-300 rounded-full focus:ring-1 focus:ring-blue-400 focus:border-blue-400 w-full"
                  />
                  {searchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" size={16} />
                  )}
                  {searchTerm && !searchLoading && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setShowSearchResults(false);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XCircle size={16} />
                    </button>
                  )}
                </form>

                {/* Mobile search results */}
                {showSearchResults && (searchResults.length > 0 || searchSuggestions.length > 0) && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-64 overflow-y-auto">
                    {searchIntent && (
                      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                        <div className="flex items-center gap-2 text-sm text-blue-700">
                          <Sparkles size={14} />
                          <span className="font-medium">AI:</span>
                          <span className="text-xs">{searchIntent}</span>
                        </div>
                      </div>
                    )}

                    {searchResults.length > 0 && (
                      <div className="py-2">
                        {searchResults.slice(0, 3).map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleSearchResultClick(product)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                          >
                            <img
                              src={product.images?.[0] || '/placeholder-product.jpg'}
                              alt={product.name}
                              className="w-8 h-8 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                ${product.moqPrices?.[0]?.price || 0}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {searchSuggestions.length > 0 && (
                      <div className="py-2 border-t border-gray-100">
                        {searchSuggestions.slice(0, 3).map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <ProductsDropdown isMobile={true} />
              
              {user ? (
                <div className="space-y-2 pt-4">
                  {user.userType === 'seller' && user.status === 'active' && (
                    <button 
                      onClick={() => {
                        navigate('/seller/dashboard');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                    >
                      <BarChart3 size={14} className="inline mr-2" />
                      Dashboard
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      navigate('/profile');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                  >
                    <User size={14} className="inline mr-2" />
                    Profile
                  </button>
                  {user.userType === 'buyer' && (
                    <>
                      <button 
                        onClick={() => {
                          navigate('/buyer-orders');
                          setMobileMenuOpen(false);
                        }}
                        className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                      >
                        <ShoppingBag size={14} className="inline mr-2" />
                        Orders
                      </button>
                      <button 
                        onClick={() => {
                          navigate('/cart');
                          setMobileMenuOpen(false);
                        }}
                        className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                      >
                        <ShoppingCart size={14} className="inline mr-2" />
                        Cart
                      </button>
                    </>
                  )}
                  <button 
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 w-full text-xs text-red-600 hover:text-red-700"
                  >
                    <LogIn size={14} className="inline mr-2" />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2 pt-4">
                  <button 
                    onClick={() => {
                      handleSellerLogin();
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                  >
                    Seller Login
                  </button>
                  <button 
                    onClick={() => {
                      handleBuyerLogin();
                      setMobileMenuOpen(false);
                    }}
                    className="text-left py-2 w-full text-xs text-gray-700 hover:text-blue-600"
                  >
                    Buyer Login
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;